import React, { useState, useEffect, useMemo } from 'react';
import { AdminProductsAPI, AdminCategoriesAPI, getProductImageUrl } from '../../Services/ECommerceAPI';
import { toast } from 'react-toastify';
import './AdminProducts.css';

const AdminProducts = () => {
    const [products, setProducts] = useState([]);
    const [categories, setCategories] = useState([]);
    const [loading, setLoading] = useState(true);
    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(15);
    const [totalCount, setTotalCount] = useState(0);
    const [showModal, setShowModal] = useState(false);
    const [editingProduct, setEditingProduct] = useState(null);
    const [productImages, setProductImages] = useState([]);
    const [newImageUrl, setNewImageUrl] = useState('');
    const [selectedFiles, setSelectedFiles] = useState([]);
    const [uploadingImages, setUploadingImages] = useState(false);
    const [formData, setFormData] = useState({
        name: '',
        description: '',
        compareAtPrice: '',
        discountPercent: '',
        stockQuantity: '',
        categoryID: '',
        isPublished: false
    });

    const filePreviews = useMemo(() => selectedFiles.map((f) => ({ name: f.name, url: URL.createObjectURL(f) })), [selectedFiles]);

    useEffect(() => {
        return () => filePreviews.forEach((p) => URL.revokeObjectURL(p.url));
    }, [filePreviews]);

    useEffect(() => {
        loadData();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [page, pageSize]);

    const loadData = async () => {
        try {
            setLoading(true);
            const [productsRes, categoriesData] = await Promise.all([
                AdminProductsAPI.getAllProducts(page, pageSize),
                AdminCategoriesAPI.getAllCategories()
            ]);

            const items = Array.isArray(productsRes)
                ? productsRes
                : (Array.isArray(productsRes?.items) ? productsRes.items : (Array.isArray(productsRes?.data) ? productsRes.data : []));

            const count = typeof productsRes?.totalCount === 'number'
                ? productsRes.totalCount
                : (typeof productsRes?.total === 'number' ? productsRes.total : (Array.isArray(productsRes) ? productsRes.length : items.length));

            setProducts(items);
            setTotalCount(count);
            setCategories(categoriesData);
        } catch (error) {
            console.error('Error loading data:', error);
            toast.error('Failed to load products');
        } finally {
            setLoading(false);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        try {
            // Validate required fields
            if (!formData.name || !formData.name.trim()) {
                toast.error('Product name is required');
                return;
            }

            if (!formData.categoryID || formData.categoryID === '') {
                toast.error('Please select a category');
                return;
            }

            const categoryIdInt = parseInt(formData.categoryID);
            if (isNaN(categoryIdInt) || categoryIdInt <= 0) {
                toast.error('Please select a valid category');
                return;
            }

            // Prepare product data with proper types
            const originalPrice = parseFloat(formData.compareAtPrice) || 0;
            const discountPct = Math.min(100, Math.max(0, parseFloat(formData.discountPercent) || 0));
            const salePrice = originalPrice * (1 - discountPct / 100);

            const productData = {
                name: formData.name.trim(),
                description: formData.description?.trim() || null,
                price: salePrice,
                compareAtPrice: discountPct > 0 && originalPrice > 0 ? originalPrice : null,
                stockQuantity: parseInt(formData.stockQuantity) || 0,
                categoryID: categoryIdInt,
                isPublished: formData.isPublished || false
            };

            // Validate price and stock
            if (originalPrice <= 0) {
                toast.error('Original price must be greater than 0');
                return;
            }

            if (productData.stockQuantity < 0) {
                toast.error('Stock quantity cannot be negative');
                return;
            }

            console.log('Sending product data:', productData);
            console.log('CategoryID in payload:', productData.categoryID, 'Type:', typeof productData.categoryID);
            console.log('Full payload JSON:', JSON.stringify(productData));

            let productId;
            if (editingProduct) {
                await AdminProductsAPI.updateProduct(editingProduct.productID, productData);
                productId = editingProduct.productID;
                toast.success('Product updated successfully');
            } else {
                const newProduct = await AdminProductsAPI.createProduct(productData);
                productId = newProduct.productID;
                toast.success('Product created successfully');
            }

            // Upload selected image files and add as product images
            if (productId && selectedFiles.length > 0) {
                setUploadingImages(true);
                try {
                    let sortOrder = productImages.length;
                    for (let i = 0; i < selectedFiles.length; i++) {
                        const file = selectedFiles[i];
                        const uploadRes = await AdminProductsAPI.uploadProductImage(file);
                        const url = uploadRes?.url || uploadRes;
                        if (url) {
                            await AdminProductsAPI.addProductImage(productId, {
                                imageUrl: url.startsWith('http') ? url : (url.startsWith('/') ? url : '/' + url),
                                altText: formData.name,
                                isPrimary: productImages.length === 0 && i === 0,
                                sortOrder: sortOrder++
                            });
                        }
                    }
                    toast.success('Images uploaded');
                } catch (err) {
                    console.error('Error uploading images:', err);
                    toast.error('Product saved but some images failed to upload');
                } finally {
                    setUploadingImages(false);
                }
            }
            
            if (productId) {
                await loadProductImages(productId);
            }
            
            setShowModal(false);
            resetForm();
            setPage(1);
            loadData();
        } catch (error) {
            console.error('Error saving product:', error);
            toast.error('Failed to save product');
        }
    };

    const resetForm = () => {
        setFormData({
            name: '',
            description: '',
            compareAtPrice: '',
            discountPercent: '',
            stockQuantity: '',
            categoryID: '',
            isPublished: false
        });
        setEditingProduct(null);
        setProductImages([]);
        setNewImageUrl('');
        setSelectedFiles([]);
    };

    const handleAddImage = async () => {
        if (!newImageUrl.trim() || !editingProduct) return;
        
        try {
            const imageData = {
                imageUrl: newImageUrl.trim(),
                altText: formData.name,
                isPrimary: productImages.length === 0,
                sortOrder: productImages.length
            };
            await AdminProductsAPI.addProductImage(editingProduct.productID, imageData);
            toast.success('Image added successfully');
            setNewImageUrl('');
            await loadProductImages(editingProduct.productID);
        } catch (error) {
            console.error('Error adding image:', error);
            toast.error('Failed to add image');
        }
    };

    const handleDeleteImage = async (imageId) => {
        if (!window.confirm('Are you sure you want to delete this image?') || !editingProduct) return;
        
        try {
            await AdminProductsAPI.deleteProductImage(editingProduct.productID, imageId);
            toast.success('Image deleted successfully');
            await loadProductImages(editingProduct.productID);
        } catch (error) {
            console.error('Error deleting image:', error);
            toast.error('Failed to delete image');
        }
    };

    const handleSetPrimary = async (imageId) => {
        if (!editingProduct) return;
        
        try {
            await AdminProductsAPI.setPrimaryImage(editingProduct.productID, imageId);
            toast.success('Primary image updated');
            await loadProductImages(editingProduct.productID);
        } catch (error) {
            console.error('Error setting primary image:', error);
            toast.error('Failed to set primary image');
        }
    };

    const loadProductImages = async (productId) => {
        try {
            const images = await AdminProductsAPI.getProductImages(productId);
            setProductImages(images || []);
        } catch (error) {
            console.error('Error loading product images:', error);
            setProductImages([]);
        }
    };

    const handleEdit = async (product) => {
        setEditingProduct(product);
        const compareAtPrice = product.compareAtPrice ?? product.CompareAtPrice;
        const price = product.price ?? product.Price;
        const discountPercent = (compareAtPrice > 0 && price < compareAtPrice)
            ? Math.round((1 - price / compareAtPrice) * 100)
            : 0;
        setFormData({
            name: product.name,
            description: product.description || '',
            compareAtPrice: (compareAtPrice ?? price ?? '').toString(),
            discountPercent: discountPercent.toString(),
            stockQuantity: product.stockQuantity,
            categoryID: product.categoryID,
            isPublished: product.isPublished
        });
        setNewImageUrl('');
        await loadProductImages(product.productID);
        setShowModal(true);
    };

    const handleDelete = async (id) => {
        if (window.confirm('Are you sure you want to delete this product?')) {
            try {
                await AdminProductsAPI.deleteProduct(id);
                toast.success('Product deleted successfully');
                setPage(1);
                loadData();
            } catch (error) {
                console.error('Error deleting product:', error);
                toast.error('Failed to delete product');
            }
        }
    };

    const handlePublish = async (id, isPublished) => {
        try {
            await AdminProductsAPI.publishProduct(id, !isPublished);
            toast.success(`Product ${!isPublished ? 'published' : 'unpublished'}`);
            loadData();
        } catch (error) {
            console.error('Error updating publish status:', error);
            toast.error('Failed to update product');
        }
    };

    const totalPages = Math.max(1, Math.ceil((totalCount || 0) / pageSize));

    return (
        <div className="admin-products">
            <div className="page-header">
                <h1>Product Management</h1>
                <button className="add-button" onClick={() => { resetForm(); setShowModal(true); }}>
                    + Add Product
                </button>
            </div>

            {loading ? (
                <div className="loading">Loading products...</div>
            ) : (
                <div className="table-card admin-table-card">
                    <div className="table-header">
                        <div className="table-title-section">
                            <h3 className="table-title">Products</h3>
                            <p className="table-subtitle">{totalCount} products</p>
                        </div>
                    </div>

                    <div className="table-container products-table-container">
                        <table className="products-table admin-table">
                            <thead>
                                <tr>
                                    <th>Name</th>
                                    <th>Category</th>
                                    <th>Price</th>
                                    <th>Stock</th>
                                    <th>Status</th>
                                    <th>Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {products.map(product => (
                                    <tr key={product.productID}>
                                        <td>{product.name}</td>
                                        <td>{product.category?.name || 'N/A'}</td>
                                        <td>${product.price.toFixed(2)}
                                            {product.compareAtPrice != null && product.compareAtPrice > product.price && (
                                                <span className="table-original-price"> (was ${product.compareAtPrice.toFixed(2)})</span>
                                            )}
                                        </td>
                                        <td>{product.stockQuantity}</td>
                                        <td>
                                            <span className={`status-badge ${product.isPublished ? 'published' : 'draft'}`}>
                                                {product.isPublished ? 'Published' : 'Draft'}
                                            </span>
                                        </td>
                                        <td className="actions">
                                            <button onClick={() => handleEdit(product)} className="edit-btn">Edit</button>
                                            <button onClick={() => handlePublish(product.productID, product.isPublished)} className="publish-btn">
                                                {product.isPublished ? 'Unpublish' : 'Publish'}
                                            </button>
                                            <button onClick={() => handleDelete(product.productID)} className="delete-btn">Delete</button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>

                    <div className="table-footer">
                        <div className="table-meta">
                            Showing <strong>{products.length}</strong> of <strong>{totalCount}</strong>
                        </div>
                        <div className="table-controls">
                            <label className="page-size">
                                Rows
                                <select
                                    value={pageSize}
                                    onChange={(e) => {
                                        setPageSize(parseInt(e.target.value, 10) || 15);
                                        setPage(1);
                                    }}
                                >
                                    <option value={10}>10</option>
                                    <option value={15}>15</option>
                                    <option value={20}>20</option>
                                    <option value={50}>50</option>
                                </select>
                            </label>
                            <div className="pagination">
                                <button
                                    type="button"
                                    className="page-btn"
                                    onClick={() => setPage((p) => Math.max(1, p - 1))}
                                    disabled={page <= 1}
                                >
                                    Prev
                                </button>
                                <span className="page-indicator">
                                    Page <strong>{page}</strong> / <strong>{totalPages}</strong>
                                </span>
                                <button
                                    type="button"
                                    className="page-btn"
                                    onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                                    disabled={page >= totalPages}
                                >
                                    Next
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {showModal && (
                <div className="modal-overlay" onClick={() => setShowModal(false)}>
                    <div className="modal-content" onClick={(e) => e.stopPropagation()}>
                        <h2>{editingProduct ? 'Edit Product' : 'Add Product'}</h2>
                        <form onSubmit={handleSubmit}>
                            <div className="form-group">
                                <label>Name *</label>
                                <input
                                    type="text"
                                    value={formData.name}
                                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                                    required
                                />
                            </div>
                            <div className="form-group">
                                <label>Description</label>
                                <textarea
                                    value={formData.description}
                                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                                    rows="4"
                                />
                            </div>
                            <div className="form-row">
                                <div className="form-group">
                                    <label>Original Price *</label>
                                    <input
                                        type="number"
                                        step="0.01"
                                        min="0"
                                        placeholder="e.g. 100.00"
                                        value={formData.compareAtPrice}
                                        onChange={(e) => setFormData({ ...formData, compareAtPrice: e.target.value })}
                                        required
                                    />
                                </div>
                                <div className="form-group">
                                    <label>Discount %</label>
                                    <input
                                        type="number"
                                        min="0"
                                        max="100"
                                        step="0.5"
                                        placeholder="0"
                                        value={formData.discountPercent}
                                        onChange={(e) => setFormData({ ...formData, discountPercent: e.target.value })}
                                    />
                                    <span className="field-hint">0–100. Sale price = Original × (1 − discount/100)</span>
                                </div>
                            </div>
                            <div className="form-group computed-price">
                                <label>Sale Price (after discount)</label>
                                <div className="computed-price-value">
                                    $
                                    {(
                                        (parseFloat(formData.compareAtPrice) || 0) *
                                        (1 - (Math.min(100, Math.max(0, parseFloat(formData.discountPercent) || 0)) / 100))
                                    ).toFixed(2)}
                                </div>
                            </div>
                            <div className="form-row">
                                <div className="form-group">
                                    <label>Stock Quantity *</label>
                                    <input
                                        type="number"
                                        value={formData.stockQuantity}
                                        onChange={(e) => setFormData({ ...formData, stockQuantity: e.target.value })}
                                        required
                                    />
                                </div>
                            </div>
                            <div className="form-group">
                                <label>Category *</label>
                                <select
                                    value={formData.categoryID}
                                    onChange={(e) => {
                                        const selectedValue = e.target.value;
                                        console.log('Category selected:', selectedValue, 'Type:', typeof selectedValue);
                                        setFormData({ ...formData, categoryID: selectedValue });
                                    }}
                                    required
                                >
                                    <option value="">Select Category</option>
                                    {categories.map(cat => (
                                        <option key={cat.categoryID} value={String(cat.categoryID)}>{cat.name}</option>
                                    ))}
                                </select>
                                {!formData.categoryID && (
                                    <span style={{ color: 'red', fontSize: '0.875rem', display: 'block', marginTop: '5px' }}>
                                        Please select a category
                                    </span>
                                )}
                            </div>
                            <div className="form-group checkbox-group">
                                <label>
                                    <input
                                        type="checkbox"
                                        checked={formData.isPublished}
                                        onChange={(e) => setFormData({ ...formData, isPublished: e.target.checked })}
                                    />
                                    Publish Product
                                </label>
                            </div>
                            
                            <div className="form-group">
                                <label>Upload product image(s)</label>
                                <input
                                    type="file"
                                    accept="image/jpeg,image/png,image/gif,image/webp"
                                    multiple
                                    onChange={(e) => setSelectedFiles(e.target.files ? Array.from(e.target.files) : [])}
                                    className="file-input"
                                />
                                {selectedFiles.length > 0 && (
                                    <div className="selected-files-preview">
                                        <span className="file-hint">{selectedFiles.length} file(s) selected. They will be uploaded when you click Save.</span>
                                        <div className="selected-files-grid">
                                            {filePreviews.map((preview, index) => (
                                                <div key={`${preview.name}-${index}`} className="selected-file-item">
                                                    <img src={preview.url} alt={preview.name} className="selected-file-thumb" />
                                                    <span className="selected-file-name" title={preview.name}>{preview.name}</span>
                                                    <button
                                                        type="button"
                                                        className="selected-file-remove"
                                                        onClick={() => setSelectedFiles((prev) => prev.filter((_, i) => i !== index))}
                                                        aria-label={`Remove ${preview.name}`}
                                                    >
                                                        ×
                                                    </button>
                                                </div>
                                            ))}
                                        </div>
                                        <button
                                            type="button"
                                            className="clear-files-btn"
                                            onClick={() => setSelectedFiles([])}
                                        >
                                            Clear all
                                        </button>
                                    </div>
                                )}
                            </div>
                            {editingProduct && (
                                <div className="form-group">
                                    <label>Product Images</label>
                                    <div className="image-management">
                                        <div className="add-image-section">
                                            <input
                                                type="text"
                                                placeholder="Or enter image URL"
                                                value={newImageUrl}
                                                onChange={(e) => setNewImageUrl(e.target.value)}
                                                className="image-url-input"
                                            />
                                            <button 
                                                type="button" 
                                                onClick={handleAddImage}
                                                className="add-image-btn"
                                                disabled={!newImageUrl.trim()}
                                            >
                                                Add Image URL
                                            </button>
                                        </div>
                                        <div className="images-grid">
                                            {productImages.map(image => (
                                                <div key={image.imageID} className="image-item">
                                                    <img 
                                                        src={getProductImageUrl(image.imageUrl)} 
                                                        alt={image.altText || formData.name}
                                                        onError={(e) => {
                                                            e.target.src = 'https://via.placeholder.com/150x150?text=Image+Error';
                                                        }}
                                                    />
                                                    {image.isPrimary && (
                                                        <span className="primary-badge">Primary</span>
                                                    )}
                                                    <div className="image-actions">
                                                        {!image.isPrimary && (
                                                            <button 
                                                                type="button"
                                                                onClick={() => handleSetPrimary(image.imageID)}
                                                                className="set-primary-btn"
                                                            >
                                                                Set Primary
                                                            </button>
                                                        )}
                                                        <button 
                                                            type="button"
                                                            onClick={() => handleDeleteImage(image.imageID)}
                                                            className="delete-image-btn"
                                                        >
                                                            Delete
                                                        </button>
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                </div>
                            )}
                            
                            <div className="modal-actions">
                                <button type="button" onClick={() => setShowModal(false)} className="cancel-btn">Cancel</button>
                                <button type="submit" className="save-btn" disabled={uploadingImages}>
                                    {uploadingImages ? 'Uploading images...' : 'Save'}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
};

export default AdminProducts;
